import React, { useState } from "react";
import { X, Server, Send, CheckCircle2, AlertCircle, RefreshCw, Key, Shield } from "lucide-react";
import { api } from "../services/api";

export default function SettingsModal({
  isOpen,
  onClose,
  status,
  onRefreshStatus,
}) {
  // MT5 Form State
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [server, setServer] = useState("Exness-MT5Real");
  const [terminalPath, setTerminalPath] = useState("");
  const [mt5Loading, setMt5Loading] = useState(false);
  const [mt5Message, setMt5Message] = useState(null);

  // Telegram Form State
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [telegramEnabled, setTelegramEnabled] = useState(true);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramMessage, setTelegramMessage] = useState(null);

  if (!isOpen) return null;

  const handleConnectMT5 = async (e) => {
    e.preventDefault();
    setMt5Loading(true);
    setMt5Message(null);
    try {
      const res = await api.connectMT5({
        account: account ? parseInt(account) : null,
        password: password || null,
        server: server || "Exness-MT5Real",
        path: terminalPath || null,
      });
      setMt5Message({
        type: res.success ? "success" : "error",
        text: res.message,
      });
      onRefreshStatus();
    } catch (err) {
      setMt5Message({
        type: "error",
        text: err.response?.data?.detail || "Lỗi kết nối MT5",
      });
    } finally {
      setMt5Loading(false);
    }
  };

  const handleDisconnectMT5 = async () => {
    setMt5Loading(true);
    try {
      const res = await api.disconnectMT5();
      setMt5Message({
        type: "success",
        text: "Đã ngắt kết nối MT5 và chuyển về chế độ Giả lập Real-time",
      });
      onRefreshStatus();
    } catch (err) {
      console.error(err);
    } finally {
      setMt5Loading(false);
    }
  };

  const handleSaveTelegram = async (e) => {
    e.preventDefault();
    setTelegramLoading(true);
    setTelegramMessage(null);
    try {
      await api.setTelegramConfig({
        bot_token: botToken,
        chat_id: chatId,
        enabled: telegramEnabled,
      });
      setTelegramMessage({
        type: "success",
        text: "Đã lưu cấu hình Telegram thành công!",
      });
    } catch (err) {
      setTelegramMessage({
        type: "error",
        text: "Lỗi lưu cấu hình Telegram",
      });
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    setTelegramLoading(true);
    setTelegramMessage(null);
    try {
      const res = await api.testTelegram();
      setTelegramMessage({
        type: res.success ? "success" : "error",
        text: res.message,
      });
    } catch (err) {
      setTelegramMessage({
        type: "error",
        text: "Lỗi gửi tin nhắn Telegram",
      });
    } finally {
      setTelegramLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400">
              <Server className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-base text-slate-100">Cấu Hình Kết Nối Máy Chủ & Telegram</h2>
              <p className="text-xs text-slate-400">Quản lý kết nối dữ liệu thị trường trực tiếp và thông báo tín hiệu</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* MT5 Current Status Badge */}
          <div className="flex items-center justify-between p-3.5 bg-slate-950/80 rounded-xl border border-slate-800">
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full ${status?.connected ? "bg-emerald-400 animate-ping" : "bg-amber-400"}`} />
              <div>
                <span className="text-xs font-bold text-slate-200 block">
                  Trạng thái: {status?.connected ? "Đã kết nối luồng Live" : "Chế độ Giả lập"}
                </span>
                <span className="text-[11px] text-slate-400 font-mono">
                  Server: {status?.server || "Default"} • Mã kết nối: {status?.account || "Auto"}
                </span>
              </div>
            </div>
            {status?.connected && (
              <button
                onClick={handleDisconnectMT5}
                disabled={mt5Loading}
                className="px-3 py-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 rounded-lg text-xs font-semibold transition"
              >
                Ngắt kết nối
              </button>
            )}
          </div>

          {/* MT5 Connection Form */}
          <form onSubmit={handleConnectMT5} className="bg-slate-950/60 p-4 rounded-xl border border-slate-800/80 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                <Shield className="w-4 h-4" />
                Kết Nối Máy Chủ Dữ Liệu Trực Tiếp
              </h3>
              <span className="text-[10px] text-slate-500">Hỗ trợ Real Market Feed / Demo</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Số tài khoản (MT5 Login ID)</label>
                <input
                  type="number"
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  placeholder="Ví dụ: 14598213"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Mật khẩu giao dịch</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Server Exness</label>
                <input
                  type="text"
                  value={server}
                  onChange={(e) => setServer(e.target.value)}
                  placeholder="Exness-MT5Real hoặc Exness-MT5Trial"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Đường dẫn terminal64.exe (Tùy chọn)</label>
                <input
                  type="text"
                  value={terminalPath}
                  onChange={(e) => setTerminalPath(e.target.value)}
                  placeholder="C:\Program Files\MetaTrader 5\terminal64.exe"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-amber-400"
                />
              </div>
            </div>

            {mt5Message && (
              <div
                className={`p-3 rounded-lg text-xs flex items-start gap-2 ${
                  mt5Message.type === "success"
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    : "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                }`}
              >
                {mt5Message.type === "success" ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                )}
                <span>{mt5Message.text}</span>
              </div>
            )}

            <div className="flex justify-between items-center pt-2">
              <span className="text-[11px] text-slate-400 italic">
                *Nếu bạn đã mở sẵn phần mềm MT5 Exness trên máy, hệ thống có thể tự động sync.
              </span>
              <button
                type="submit"
                disabled={mt5Loading}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs rounded-lg transition shadow-md shadow-emerald-500/20 cursor-pointer"
              >
                {mt5Loading ? "Đang kết nối..." : "Kết Nối MT5"}
              </button>
            </div>
          </form>

          {/* Telegram Settings Form */}
          <form onSubmit={handleSaveTelegram} className="bg-slate-950/60 p-4 rounded-xl border border-slate-800/80 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-sky-400 flex items-center gap-1.5">
                <Send className="w-4 h-4" />
                Cấu Hình Telegram Bắn Tín Hiệu & Cảnh Báo
              </h3>
              <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={telegramEnabled}
                  onChange={(e) => setTelegramEnabled(e.target.checked)}
                  className="accent-sky-400 rounded"
                />
                <span>Kích hoạt Telegram</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Telegram Bot Token (từ @BotFather)</label>
                <input
                  type="text"
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder="123456789:ABCdefGhIJKlmNoPQRstuvwx"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-sky-400"
                />
              </div>

              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Telegram Chat ID (của bạn hoặc nhóm)</label>
                <input
                  type="text"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  placeholder="Ví dụ: 987654321 hoặc -100123456"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-sky-400"
                />
              </div>
            </div>

            {telegramMessage && (
              <div
                className={`p-3 rounded-lg text-xs flex items-start gap-2 ${
                  telegramMessage.type === "success"
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    : "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                }`}
              >
                {telegramMessage.type === "success" ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                )}
                <span>{telegramMessage.text}</span>
              </div>
            )}

            <div className="flex justify-end items-center gap-2 pt-2">
              <button
                type="button"
                onClick={handleTestTelegram}
                disabled={telegramLoading || !botToken || !chatId}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-sky-400 border border-slate-700 font-semibold text-xs rounded-lg transition cursor-pointer"
              >
                {telegramLoading ? "Đang thử..." : "Test Gửi Thử"}
              </button>
              <button
                type="submit"
                disabled={telegramLoading}
                className="px-4 py-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs rounded-lg transition shadow-md shadow-sky-500/20 cursor-pointer"
              >
                Lưu Cài Đặt Telegram
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}